/**
 * SHOP KIM CƯƠNG — mua mảnh ghép, chiêu mới và nhân vật bằng 💎.
 *
 * VÌ SAO CẦN SHOP NÀY: mảnh (shard) rơi ra rất chậm, còn kim cương là phần thưởng
 * "làm được gì đó ngay". Shop là van xả: ai muốn nhanh thì trả 💎, ai kiên nhẫn thì
 * cày mảnh — hai đường đến cùng một đích nên không phá cân bằng.
 *
 * TOÀN BỘ LÀ DỮ LIỆU: `DIAMOND_SHOP` sinh ra bằng vòng lặp từ `CLASSES` / `EXTRA_SKILLS`,
 * nên thêm 1 class hay 1 chiêu mới là shop TỰ có món tương ứng — không phải sửa file này.
 *
 * ⚠️ PHỤ KIỆN ĐÃ XOÁ HẲN (người dùng chốt: *"xoá hẳn phụ kiện"*). Mọi món `shardAcc` /
 * `accessory`, bảng giá theo vàng và hằng số `SHARD_ACC_PACK_COST` không còn ở đây.
 * Tương ứng, `CraftState.shards` chỉ còn `hero` + `skill` (xem `sim/craft.ts`).
 */
import { CLASSES } from './classes';
import { EXTRA_SKILLS } from './skills-extra';
import type { ClassId } from '../sim/types';

/*
 * ⚠️ Hằng số CHÉP TẠI CHỖ, không import từ `sim/meta.ts`: file này được dựng NGAY lúc eval module,
 * mà `sim/meta.ts` lại import `data/classes.ts` ⇒ import vòng làm giá trị còn `undefined` (đúng
 * BẪY 1 đã ghi trong `sim/craft.ts`). `tests/i18n.test.ts` + `tests/craft.test.ts` khoá việc hai
 * bên khớp nhau.
 */
const UNLOCK_SHARDS = 30;
const UPGRADE_SHARDS = 10;

export type ShopKind = 'shardClass' | 'shardSkill' | 'skill';

export interface ShopItem {
  id: string;
  kind: ShopKind;
  name: string;
  desc: string;
  cost: number;
  target?: string;
  /** kèm số lượng cho loại shard */
  amount?: number;
}

// ------------------------------------------------------------------ bảng giá
/**
 * GIÁ ĐÃ CÂN THEO ĐƯỜNG GHÉP MẢNH (xem `sim/craft.ts`):
 *  · ghép 1 CHIÊU = 25 mảnh chiêu; 1 túi = 10 mảnh giá 45💎 ⇒ cày mảnh tốn ~113💎.
 *    Mua thẳng đặt **260💎** (≈2,3×) ⇒ "mua thẳng luôn đắt hơn ghép" — đúng yêu cầu.
 *  · ghép 1 NHÂN VẬT = 30 mảnh hero; 1 túi = 10 mảnh giá 40💎 ⇒ ~120💎. Mua thẳng **400💎**.
 *    ⚠️ Sổ nhân vật tối đa 5 (`MAX_ROSTER` trong `sim/meta.ts`) nên mua thẳng cũng bị chặn
 *    khi sổ đầy — `buyShopItem` trả `ok:false` TRƯỚC khi trừ 💎.
 */
const SHARD_PACK_AMOUNT = 10;
const SHARD_SKILL_PACK_COST = 45;
const SHARD_CLASS_PACK_COST = 40;
const SKILL_DIRECT_COST = 260;

/**
 * ⚠️ NGƯỜI DÙNG CHỐT: *"ở shop đều phải thể hiện đơn vị (Ví dụ: Giá cho 25 mảnh là x kim cương
 * thì phải có số 25 ở mảnh để người chơi biết)"*.
 *
 * Vì vậy câu mô tả/ tên món PHẢI in ra: SỐ MẢNH trong túi · SỐ MẢNH cần để mở khoá/ nâng cấp, và
 * giá. Không viết kiểu "Túi mảnh nhân vật" chung chung nữa.
 */

// ⚠️ Khai báo class tại chỗ (không mượn `CLASS_ORDER`) vì `DIAMOND_SHOP` được dựng
// NGAY lúc eval module — mọi import vòng sẽ làm `CLASS_ORDER` còn `undefined` ở đây.
/*
 * 7 CLASS: 5 class gốc + 2 class MỚI (Thợ mộc · Thợ lặn). Người dùng chốt *"Tạo sẵn cho tôi 2
 * class trong shop"* và *"Mảnh ... bao gồm cả mảnh của class đã và chưa unlock"* ⇒ 2 class mới
 * PHẢI có túi mảnh ở đây, nếu không thì không có cách nào mở khoá chúng.
 */
const SHOP_CLASSES: ClassId[] = ['strength', 'agility', 'street', 'army', 'mother', 'carpenter', 'diver'];

/** 5 túi MẢNH CHIÊU — mỗi class 1 túi (mảnh chiêu tính theo class). */
const SHARD_SKILL_ITEMS: ShopItem[] = SHOP_CLASSES.map((c) => ({
  id: `shard-skill-${c}`,
  kind: 'shardSkill' as const,
  name: `Túi mảnh chiêu · ${CLASSES[c].name}`,
  desc: `+${SHARD_PACK_AMOUNT} mảnh chiêu ${CLASSES[c].name} (ghép 1 chiêu cần 25)`,
  cost: SHARD_SKILL_PACK_COST,
  target: c,
  amount: SHARD_PACK_AMOUNT,
}));

/**
 * TÚI MẢNH CLASS — mỗi class 1 túi, cho CẢ 5 class gốc LẪN 2 class chưa mở khoá.
 *
 * Người dùng chốt: *"Mảnh nhân vật thay bằng mảnh class, bao gồm cả mảnh của class đã và chưa
 * unlock, nếu class chưa unlock đủ mảnh, khi đủ mảnh sẽ mở khoá class, nếu đã unlock và đủ mảnh,
 * người dùng có thể dùng để nâng cấp tướng"*.
 */
const SHARD_CLASS_ITEMS: ShopItem[] = SHOP_CLASSES.map((c) => ({
  id: `shard-class-${c}`,
  kind: 'shardClass' as const,
  // TÊN + MÔ TẢ có SỐ MẢNH và SỐ CẦN dùng — đúng yêu cầu "phải thể hiện đơn vị"
  name: `Túi ${SHARD_PACK_AMOUNT} mảnh ${CLASSES[c].name}`,
  desc: `+${SHARD_PACK_AMOUNT} mảnh class ${CLASSES[c].name} · mở khoá cần ${UNLOCK_SHARDS}, nâng cấp tướng cần ${UPGRADE_SHARDS}`,
  cost: SHARD_CLASS_PACK_COST,
  target: c,
  amount: SHARD_PACK_AMOUNT,
}));

/** 15 CHIÊU MỚI — mua thẳng bằng 💎, luôn đắt hơn đường ghép mảnh. */
const SKILL_ITEMS: ShopItem[] = SHOP_CLASSES.flatMap((c) =>
  (EXTRA_SKILLS[c] ?? []).map((sk) => ({
    id: `skill-${c}-${sk.id}`,
    kind: 'skill' as const,
    name: sk.name,
    desc: `${sk.desc} · dùng ngay, không cần ghép`,
    cost: SKILL_DIRECT_COST,
    // `target` chỉ là ID CHIÊU: id chiêu vốn đã duy nhất toàn cục, `buyShopItem` tự
    // dò ra class sở hữu bằng `classOfSkill()`. Nhét "class:skill" vào target sẽ phải
    // parse chuỗi — dễ vỡ khi id có dấu ':'.
    target: sk.id,
  })),
);

export const DIAMOND_SHOP: ShopItem[] = [
  ...SHARD_CLASS_ITEMS,
  ...SHARD_SKILL_ITEMS,
  ...SKILL_ITEMS,
];

/** Tìm món trong shop. Không thấy ⇒ `undefined` (KHÔNG ném lỗi). */
export function shopItemById(id: string): ShopItem | undefined {
  return DIAMOND_SHOP.find((i) => i.id === id);
}
